import * as React from "react";
import blurDataUrl from "@utils/shimmer";
import fetch from "isomorphic-fetch";
import jwt_decode, { JwtPayload } from "jwt-decode";
import { NextPageWithLayout } from "@pages/_app";
import HomeLayout from "@layouts/home";
import Link from "next/link";
import Image from "next/image";
import Icon from "@components/icon";
import { v4 as uuidv4 } from "uuid";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import { GetServerSideProps } from "next";
import {
  cacheExchange,
  useQuery,
  ssrExchange,
  makeOperation,
  Operation,
  dedupExchange,
  fetchExchange,
} from "urql";
import { authExchange } from "@urql/exchange-auth";
import { withUrqlClient, initUrqlClient } from "next-urql";
import {
  deleteAccessCookie,
  deleteRefreshCookie,
  getAccessCookie,
  getRefreshCookie,
  setAccessCookie,
  setRefreshCookie,
} from "@utils/storage";
import {
  GetInawoVendorServiceCategoryQuery,
  GetPinnedVendorServicesQueryVariables,
  RefreshQuery,
} from "@graphql/types";
import { AuthState } from "@utils/auth";
import { GetInawoVendorServiceCategory } from "@graphql/queries/home.query";
import toast from "react-hot-toast";
import Head from "next/head";

type Props = {
  refreshPage: boolean;
};

const Search: NextPageWithLayout<Props> = (props) => {
  React.useEffect(() => {
    if (props.refreshPage) {
      toast.error(<p className="toast">Your session has expired.</p>, {
        duration: 10000,
      });

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }, [props.refreshPage]);

  const [searchTerm, setSearchTerm] = React.useState("");

  const router = useRouter();

  const handleSearch = () => {
    router.push(`/search/${searchTerm}`);
  };

  const [categoryResult] = useQuery<
    GetInawoVendorServiceCategoryQuery,
    GetPinnedVendorServicesQueryVariables
  >({ query: GetInawoVendorServiceCategory });

  return (
    <>
      <Head>
        <title>Inawo Vendors | Search</title>
        <meta name="title" content="Inawo Vendors | Search" />
        <meta name="description" content="Inawo Vendors Search" />
      </Head>

      <div className="w-full">
        <div className="w-full pt-8 flex justify-start items-center gap-2 text-inawo-black">
          <Link href={"/"}>
            <a className="p-1.5 bg-transparent transition hover:bg-gray-50 rounded-md">
              <Icon iconName="arrowLeft" />
            </a>
          </Link>
          <p className="uppercase tracking-widest font-light font-sfpro text-base xs:text-lg">
            Search
          </p>
        </div>
        <div className="bg-white focus-within:ring-4 focus-within:ring-inawo-green/50 transition text-inawo-black pl-4 pr-2 py-2 font-sfpro my-5 flex justify-start items-center rounded-xl">
          <input
            type={"text"}
            placeholder="Search vendors"
            className="p-0 m-0 border-0 font-sfpro font-light placeholder:font-light focus:ring-0 flex-1"
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <div
            onClick={() => handleSearch()}
            className="cursor-pointer p-1.5 rounded-md bg-transparent hover:bg-gray-100 transition"
          >
            <Icon iconName="search" />
          </div>
        </div>
        <p className="mb-5 font-sfpro text-lg sm:text-xl">Browse Vendors</p>
        <div className="w-full grid grid-cols-245 gap-6">
          {categoryResult.fetching ? (
            <></>
          ) : categoryResult.error?.message ? (
            <>An error occured</>
          ) : (
            !!categoryResult.data?.getInawoVendorServiceCategory &&
            categoryResult.data?.getInawoVendorServiceCategory.map((item) => (
              <Link key={uuidv4()} href={`/categories/${item?.id}`}>
                <a className="p-6 bg-[#057C68] relative h-48 rounded-2xl overflow-hidden">
                  <motion.div
                    initial={{ scale: 1 }}
                    whileHover={{ scale: 1.1 }}
                    className="absolute z-[0] brightness-[0.8] right-0 bottom-0 top-0 left-0"
                  >
                    <Image
                      src={item?.Images![0]!}
                      alt={item?.Name}
                      layout="fill"
                      blurDataURL={blurDataUrl(400, 400)}
                      quality={75}
                      placeholder="blur"
                      priority
                      sizes="30vw"
                      objectFit="cover"
                      objectPosition={"center"}
                    />
                  </motion.div>
                  <p className="font-sfpro absolute text-white z-[2] text-lg sm:text-xl">
                    {item?.Name}
                  </p>
                </a>
              </Link>
            ))
          )}
        </div>
      </div>
    </>
  );
};

Search.getLayout = function getLayout(page: React.ReactElement) {
  return <HomeLayout>{page}</HomeLayout>;
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { req, res } = ctx;
  const accessToken = getAccessCookie({ req, res });
  let refreshPage = false;
  const ssrCache = ssrExchange({ isClient: false });
  const client = initUrqlClient(
    {
      url: process.env.NEXT_PUBLIC_API_URL!,
      requestPolicy: "cache-and-network",
      fetchOptions: {
        headers: {
          "x-user-token": accessToken ? `${accessToken}` : "",
        },
      },
      exchanges: [
        dedupExchange,
        cacheExchange,
        authExchange({
          addAuthToOperation: ({
            authState,
            operation,
          }: {
            authState: AuthState;
            operation: Operation;
          }) => {
            // the token isn't in the auth state, return the operation without changes
            if (!authState || !authState.accessToken) {
              return operation;
            }

            // fetchOptions can be a function (See Client API) but you can simplify this based on usage
            const fetchOptions =
              typeof operation.context.fetchOptions === "function"
                ? operation.context.fetchOptions()
                : operation.context.fetchOptions || {};

            return makeOperation(operation.kind, operation, {
              ...operation.context,
              requestPolicy: "cache-and-network",
              fetchOptions: {
                ...fetchOptions,
                headers: {
                  ...fetchOptions.headers,
                  "x-user-token": authState.accessToken
                    ? `${authState.accessToken}`
                    : "",
                },
              },
            });
          },
          willAuthError: ({ authState }) => {
            if (!authState) return true;
            // e.g. check for expiration, existence of auth etc
            const accessTokenDecoded = jwt_decode<JwtPayload>(
              authState.accessToken as string
            );
            const refreshTokenDecoded = jwt_decode<JwtPayload>(
              authState.refreshToken as string
            );

            // if access token will almost expire
            if (
              !!accessTokenDecoded?.exp &&
              Date.now() >= accessTokenDecoded?.exp * 1000 - 5000
            ) {
              return true;
            }

            // if refresh token will almost expire
            if (
              !!refreshTokenDecoded?.exp &&
              Date.now() >= refreshTokenDecoded?.exp * 1000 - 5000
            ) {
              return true;
            }
            return false;
          },
          didAuthError: ({ error }) => {
            // check if the error was an auth error (this can be implemented in various ways, e.g. 401 or a special error code)
            return error.graphQLErrors.some((e) =>
              e.message.includes("unauthenticated")
            );
          },
          getAuth: async ({ authState }) => {
            // for initial launch, fetch the auth state from storage (local storage, async storage etc)
            if (!authState) {
              const accessToken = getAccessCookie({ req, res }) as string;
              const refreshToken = getRefreshCookie({ req, res }) as string;

              if (accessToken && refreshToken) {
                return { accessToken, refreshToken };
              }

              return null;
            }

            /**
             * the following code gets executed when an auth error has occurred
             * we should refresh the token if possible and return a new auth state
             * If refresh fails, we should log out
             **/

            const resultInitial = await fetch(
              process.env.NEXT_PUBLIC_API_URL!,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  query: `
										query Refresh($refresh_token: String!) {
								refresh(refresh_token: $refresh_token) {
									refresh_token
									access_token
									access_expiry
								}
							}
					`,
                  variables: {
                    refresh_token: authState.refreshToken,
                  },
                }),
              }
            );

            const result: { data: RefreshQuery; errors: any } =
              await resultInitial.json();

            if (result.data?.refresh) {
              // save the new tokens in storage for next restart
              setRefreshCookie(result.data.refresh.refresh_token, {
                req,
                res,
              });
              setAccessCookie(result.data.refresh.access_token, { req, res });
              return {
                accessToken: result.data.refresh.access_token,
                refreshToken: result.data.refresh.refresh_token,
              };
            }

            // otherwise, if refresh fails, log clear storage and log out
            console.log("Search page refresh error :>> ", result);
            deleteAccessCookie({ req, res });
            deleteRefreshCookie({ req, res });
            refreshPage = true;

            return null;
          },
        }),
        ssrCache,
        fetchExchange,
      ],
    },
    false
  );

  try {
    if (client) {
      // This query is used to populate the cache for the query used on this page.
      await client
        .query<
          GetInawoVendorServiceCategoryQuery,
          GetPinnedVendorServicesQueryVariables
        >(GetInawoVendorServiceCategory)
        .toPromise();
    }
  } catch (error) {
    console.log("Vendors GetInawoVendorServiceCategory Error :>> ", error);
  }

  return {
    props: {
      urqlState: ssrCache.extractData(),
      refreshPage,
    },
  };
};

export default withUrqlClient(
  (_ssr) => ({
    url: process.env.NEXT_PUBLIC_API_URL!,
    requestPolicy: "cache-and-network",
    fetchOptions: () => {
      return {
        headers: {
          "x-user-token": getAccessCookie() ? `${getAccessCookie()}` : "",
        },
      };
    },
  }),
  { ssr: false }
)(Search);
